import React, { useRef, useEffect, useState } from 'react'
import { useChat } from 'ai/react'
import { Button } from './ui/button'
import { Textarea } from './ui/textarea'
import { Card } from './ui/card'
import { Loader2, Send, AlertTriangle, LightbulbIcon } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Define types for message parts based on AI SDK
interface MessagePart {
    type: string;
    [key: string]: any;
}

// Define reasoning content interface for different providers
interface ReasoningContent {
    steps?: string[];
    reasoning?: string;
    content?: string;
}

// Define the message interface based on what we know is returned by useChat
interface ChatMessage {
    id: string;
    content: string;
    role: 'user' | 'assistant' | 'system';
    parts?: MessagePart[];
    reasoning_content?: string; // DeepSeek format
    reasoningContent?: ReasoningContent; // OpenAI format
}

export const ChatBot: React.FC<{ endpoint?: 'chat' | 'asking-question', aiConsultDetailId?: number, initialMessages?: ChatMessage[] }> = ({ endpoint = 'chat', aiConsultDetailId = 0, initialMessages = [] as ChatMessage[] }) => {
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    const endOfMessagesRef = useRef<HTMLDivElement>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [provider, setProvider] = useState<string>('grok')
    // Removed unused autoScrollEnabled state to fix lint
    const userScrolledRef = useRef(false)

    // Get environment information
    const environment = import.meta.env.VITE_ENV || 'local'
    const apiUrl = import.meta.env.VITE_CHAT_API_URL || 'http://localhost:3000'

    // Format API URL correctly with endpoint
    const chatEndpoint = apiUrl.endsWith(`/${endpoint}`) ? apiUrl : `${apiUrl}/${endpoint}`

    // Save chat history to localStorage
    const saveChatHistory = (messages: ChatMessage[]) => {
        try {
            localStorage.setItem('chatHistory', JSON.stringify(messages))
        } catch (error) {
            console.error('Error saving chat history:', error)
        }
    }

    // Use AI SDK React hook
    const {
        messages,
        input,
        handleInputChange,
        handleSubmit,
        isLoading,
        // reload,
        // setInput
    } = useChat({
        api: chatEndpoint,
        initialMessages: initialMessages,
        body: {
            // customPrompt: '你是一個醫療顧問助手，你的任務是提供醫療建議，並協助預約醫生。請提供專業、有幫助的回應。 always respond in traditional chinese.'
            provider: provider, // Pass the selected provider to the API
            aiConsultDetailId: aiConsultDetailId
        },
        // @ts-ignore - streamMode is supported in newer AI SDK versions
        streamMode: 'text',
        headers: {
            'Cache-Control': 'no-cache, no-transform',
            'X-Accel-Buffering': 'no',
        },
        onError: (error) => {
            console.error('Chat error:', error);
            setErrorMessage(error.message || 'Unknown error');
        }
    })

    // Save messages to localStorage whenever they change
    useEffect(() => {
        if (messages.length > 0) {
            saveChatHistory(messages)
        }
    }, [messages])

    // Helper to check if user is at bottom (mobile safe)
    const isUserAtBottom = () => {
        const container = messagesContainerRef.current;
        if (!container) return true;
        return Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 60;
    };

    // On scroll/touch/wheel, mark user as having scrolled
    const handleUserScroll = () => {
        // Only mark as scrolled if user is not at bottom
        if (!isUserAtBottom()) {
            userScrolledRef.current = true;
        } else {
            userScrolledRef.current = false;
        }
    };

    // Scroll to bottom on mount and when messages change
    useEffect(() => {
        const shouldScrollToBottom = !userScrolledRef.current || isUserAtBottom();

        if (shouldScrollToBottom) {
            // Use requestAnimationFrame to ensure DOM is ready
            requestAnimationFrame(() => {
                endOfMessagesRef.current?.scrollIntoView({ behavior: messages.length > 0 ? 'smooth' : 'auto' });
            });
        }
    }, [messages, isLoading]);

    // Handle pressing enter to send
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>)
        }
    }

    // Render different types of message parts
    const renderMessagePart = (message: any, part: MessagePart, index: number) => {
        const isUserMessage = message.role === 'user'

        switch (part.type) {
            case 'reasoning':
                return (
                    <div key={`${message.id}-part-${index}`} className={`mt-2 p-2 rounded-md border border-amber-300 ${isUserMessage ? 'bg-blue-800 bg-opacity-50' : 'bg-gray-100'}`}>
                        <div className="flex items-center gap-1 text-sm font-medium mb-1">
                            <LightbulbIcon className="h-4 w-4" />
                            <span className={isUserMessage ? 'text-gray-100' : 'text-gray-700'}>思考過程:</span>
                        </div>
                        <div className={`text-sm ${isUserMessage ? 'text-gray-100' : 'text-gray-700'}`}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                            >
                                {part.reasoning}
                            </ReactMarkdown>
                        </div>
                    </div>
                )
            case 'text':
                return (
                    <ReactMarkdown
                        key={`${message.id}-part-${index}`}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            a: ({ node, className, children, ...props }: any) => (
                                <a
                                    className={`${isUserMessage ? "text-cyan-300 underline" : "text-blue-700 underline"} ${className || ''}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    {...props}
                                >
                                    {children}
                                </a>
                            ),
                            code: ({ node, inline, className, children, ...props }: any) => {
                                return inline ?
                                    <code
                                        className={`${isUserMessage ? "bg-blue-700 text-white" : "bg-gray-300 text-gray-900"} px-1 py-0.5 rounded ${className || ''}`}
                                        {...props}
                                    >
                                        {children}
                                    </code> :
                                    <code className={`${className || ''} ${isUserMessage ? "text-white" : "text-gray-900"}`} {...props}>
                                        {children}
                                    </code>
                            },
                            pre: ({ node, children, ...props }: any) => (
                                <pre
                                    className={`${isUserMessage ? "bg-gray-800 text-white" : "bg-gray-200 text-gray-900"} p-2 rounded-md my-1`}
                                    {...props}
                                >
                                    {children}
                                </pre>
                            ),
                            p: ({ node, className, children, ...props }: any) => (
                                <p
                                    className={`${isUserMessage ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                    {...props}
                                >
                                    {children}
                                </p>
                            ),
                            ul: ({ node, className, children, ...props }: any) => (
                                <ul
                                    className={`${isUserMessage ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                    {...props}
                                >
                                    {children}
                                </ul>
                            ),
                            li: ({ node, className, children, ...props }: any) => (
                                <li
                                    className={`${isUserMessage ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                    {...props}
                                >
                                    {children}
                                </li>
                            ),
                            h1: ({ node, className, children, ...props }: any) => (
                                <h1
                                    className={`${isUserMessage ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                    {...props}
                                >
                                    {children}
                                </h1>
                            ),
                            h2: ({ node, className, children, ...props }: any) => (
                                <h2
                                    className={`${isUserMessage ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                    {...props}
                                >
                                    {children}
                                </h2>
                            ),
                            h3: ({ node, className, children, ...props }: any) => (
                                <h3
                                    className={`${isUserMessage ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                    {...props}
                                >
                                    {children}
                                </h3>
                            )
                        }}
                    >
                        {part.text}
                    </ReactMarkdown>
                )
            case 'tool-invocation':
                return (
                    <div key={`${message.id}-part-${index}`} className={`mt-2 p-2 rounded-md ${isUserMessage ? 'bg-blue-800 bg-opacity-50' : 'bg-gray-200'}`}>
                        <div className="flex items-center gap-1 text-sm font-medium mb-1">
                            <span className={isUserMessage ? 'text-gray-100' : 'text-gray-700'}>工具使用: {part.toolInvocation.toolName}</span>
                        </div>
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <Card className="w-full h-full flex flex-col bg-white overflow-hidden">

            <div className="p-4 bg-secondary text-secondary-content flex justify-between items-center">
                <div className="flex flex-col">
                    <h2 className="text-lg font-semibold">GoodDoctor AI 顧問</h2>
                    <div className="flex items-center gap-2">
                        {
                            environment.toUpperCase() != 'PROD' ?
                                <></>
                                :
                                <span className="text-xs opacity-90">ENV: {environment.toUpperCase()}</span>

                        }
                        {isLoading && (
                            <span className="text-xs text-warning flex items-center">
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                處理中...
                            </span>
                        )}
                        {!isLoading && !errorMessage && (
                            <span className="text-xs text-success">已連接</span>
                        )}
                        {errorMessage && (
                            <span className="text-xs text-error">連接錯誤</span>
                        )}
                    </div>
                </div>
            </div>

            <div
                className="flex-1 p-1 sm:p-2 md:p-4 overflow-x-hidden"
                id="chat-messages-container"
            >
                {errorMessage && (
                    <div className="bg-red-100 text-red-800 p-3 mb-3 rounded-md flex items-start">
                        <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-bold">連接錯誤:</p>
                            <p className="text-sm">{errorMessage}</p>
                        </div>
                    </div>
                )}

                <div className='overflow-y-auto overflow-x-hidden resize-y'
                    ref={messagesContainerRef}
                    onScroll={handleUserScroll}
                    onTouchStart={handleUserScroll}
                    onWheel={handleUserScroll}
                    style={{
                        height: "30vh",
                        minHeight: "15vh",
                        maxHeight: "80vh"
                    }}
                >
                    <div className="space-y-4">
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex ${message.role === 'user'
                                    ? 'justify-end'
                                    : 'justify-start'
                                    }`}
                                data-testid={`message-${message.id}`}
                            >
                                <div
                                    className={`
                                max-w-[85%] sm:max-w-[80%] p-2 sm:p-3 rounded-lg break-words
                                ${message.role === 'user'
                                            ? 'bg-primary text-primary-content'
                                            : 'bg-gray-100 text-gray-900'
                                        }
                            `}
                                >
                                    <div className="prose">
                                        {/* Handle different message formats from various providers */}
                                        {(message as ChatMessage).parts && (message as ChatMessage).parts!.length > 0 ? (
                                            (message as ChatMessage).parts!.map((part: MessagePart, index: number) =>
                                                renderMessagePart(message, part, index)
                                            )
                                        ) : (message as ChatMessage).reasoning_content ? (
                                            <>
                                                <div className="mb-3 p-2 rounded-md border border-amber-300 bg-gray-100">
                                                    <div className="flex items-center gap-1 text-sm font-medium mb-1">
                                                        <LightbulbIcon className="h-4 w-4" />
                                                        <span className="text-gray-700">DeepSeek 思考過程:</span>
                                                    </div>
                                                    <div className="text-sm text-gray-700">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                            {(message as ChatMessage).reasoning_content}
                                                        </ReactMarkdown>
                                                    </div>
                                                </div>
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm]}
                                                    components={{
                                                        a: ({ node, className, children, ...props }: any) => (
                                                            <a
                                                                className={`${message.role === 'user' ? "text-cyan-300 underline" : "text-blue-700 underline"} ${className || ''}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                {...props}
                                                            >
                                                                {children}
                                                            </a>
                                                        ),
                                                        code: ({ node, inline, className, children, ...props }: any) => {
                                                            return inline ?
                                                                <code
                                                                    className={`${message.role === 'user' ? "bg-blue-700 text-white" : "bg-gray-300 text-gray-900"} px-1 py-0.5 rounded ${className || ''}`}
                                                                    {...props}
                                                                >
                                                                    {children}
                                                                </code> :
                                                                <code className={`${className || ''} ${message.role === 'user' ? "text-white" : "text-gray-900"}`} {...props}>
                                                                    {children}
                                                                </code>
                                                        },
                                                        pre: ({ node, children, ...props }: any) => (
                                                            <pre
                                                                className={`${message.role === 'user' ? "bg-gray-800 text-white" : "bg-gray-200 text-gray-900"} p-2 rounded-md my-1`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </pre>
                                                        ),
                                                        p: ({ node, className, children, ...props }: any) => (
                                                            <p
                                                                className={`${message.role === 'user' ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </p>
                                                        ),
                                                        ul: ({ node, className, children, ...props }: any) => (
                                                            <ul
                                                                className={`${message.role === 'user' ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </ul>
                                                        ),
                                                        li: ({ node, className, children, ...props }: any) => (
                                                            <li
                                                                className={`${message.role === 'user' ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </li>
                                                        ),
                                                        h1: ({ node, className, children, ...props }: any) => (
                                                            <h1
                                                                className={`${message.role === 'user' ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </h1>
                                                        ),
                                                        h2: ({ node, className, children, ...props }: any) => (
                                                            <h2
                                                                className={`${message.role === 'user' ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </h2>
                                                        ),
                                                        h3: ({ node, className, children, ...props }: any) => (
                                                            <h3
                                                                className={`${message.role === 'user' ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </h3>
                                                        )
                                                    }}
                                                >
                                                    {message.content}
                                                </ReactMarkdown>
                                            </>
                                        ) : (
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    a: ({ node, className, children, ...props }: any) => (
                                                        <a
                                                            className={`${message.role === 'user' ? "text-cyan-300 underline" : "text-blue-700 underline"} ${className || ''}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            {...props}
                                                        >
                                                            {children}
                                                        </a>
                                                    ),
                                                    code: ({ node, inline, className, children, ...props }: any) => {
                                                        return inline ?
                                                            <code
                                                                className={`${message.role === 'user' ? "bg-blue-700 text-white" : "bg-gray-300 text-gray-900"} px-1 py-0.5 rounded ${className || ''}`}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </code> :
                                                            <code className={`${className || ''} ${message.role === 'user' ? "text-white" : "text-gray-900"}`} {...props}>
                                                                {children}
                                                            </code>
                                                    },
                                                    pre: ({ node, children, ...props }: any) => (
                                                        <pre
                                                            className={`${message.role === 'user' ? "bg-gray-800 text-white" : "bg-gray-200 text-gray-900"} p-2 rounded-md my-1`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </pre>
                                                    ),
                                                    p: ({ node, className, children, ...props }: any) => (
                                                        <p
                                                            className={`${message.role === 'user' ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </p>
                                                    ),
                                                    ul: ({ node, className, children, ...props }: any) => (
                                                        <ul
                                                            className={`${message.role === 'user' ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </ul>
                                                    ),
                                                    li: ({ node, className, children, ...props }: any) => (
                                                        <li
                                                            className={`${message.role === 'user' ? "text-gray-100" : "text-gray-900"} ${className || ''}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </li>
                                                    ),
                                                    h1: ({ node, className, children, ...props }: any) => (
                                                        <h1
                                                            className={`${message.role === 'user' ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </h1>
                                                    ),
                                                    h2: ({ node, className, children, ...props }: any) => (
                                                        <h2
                                                            className={`${message.role === 'user' ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </h2>
                                                    ),
                                                    h3: ({ node, className, children, ...props }: any) => (
                                                        <h3
                                                            className={`${message.role === 'user' ? "text-gray-50" : "text-gray-900"} ${className || ''}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </h3>
                                                    )
                                                }}
                                            >
                                                {message.content}
                                            </ReactMarkdown>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Typing indicator */}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-gray-100 text-gray-900 p-3 rounded-lg flex items-center space-x-2">
                                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                    <span className="text-sm">AI 正在思考中...</span>
                                </div>
                            </div>
                        )}
                        <div ref={endOfMessagesRef} />
                    </div>
                </div>
            </div>

            <div className="p-2 sm:p-3 border-t">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-sm">AI 模型:</div>
                    <div className="flex gap-2">
                        <Button
                            variant={provider === 'grok' ? 'default' : 'outline'}
                            className="h-8 text-xs px-2"
                            onClick={() => setProvider('grok')}
                        >
                            Grok
                        </Button>
                        <Button
                            variant={provider === 'openai' ? 'default' : 'outline'}
                            className="h-8 text-xs px-2"
                            onClick={() => setProvider('openai')}
                        >
                            OpenAI
                        </Button>
                        <Button
                            variant={provider === 'deepseek' ? 'default' : 'outline'}
                            className="h-8 text-xs px-2"
                            onClick={() => setProvider('deepseek')}
                        >
                            DeepSeek
                        </Button>
                    </div>
                </div>
                <form onSubmit={handleSubmit} className="flex items-center gap-2">
                    <Textarea
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="輸入您的問題..."
                        className="flex-1 min-h-10 max-h-32 resize-none text-base" // enforce 16px font-size to prevent iOS zoom
                        rows={1}
                        disabled={isLoading}
                        data-testid="chat-input"
                    />
                    <Button
                        type="submit"
                        className="h-12 w-12 p-2 flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white shadow-md rounded-lg transition-colors flex items-center justify-center"
                        disabled={isLoading || !input.trim()}
                        data-testid="send-button"
                    >
                        <Send className="h-5 w-5 stroke-[2.5px]" />
                    </Button>
                </form>
            </div>
        </Card>
    )
}
